import { StateCreator } from 'zustand'
import { CalendarState, ResetAdmissionTypeProps, SellerSlice, SellerSliceState } from '@/types/store.types'
import { AdmissionTypeDefault, PaymentMethodType } from '@/types/general.types'
import TXT from '@/messages'
import { setBrowserCookie } from '@/utils/browser.utils'
import { GUEST_CART_IDS_KEY } from '@/config/cookies.config'

/**
 * Represents the default state of the calendar.
 */
export const calendarStateDefault: CalendarState = {
  loading: false,
  showKeys: false,
  todaySoldOut: false, // TODO: move out of zustand
  queryTimeslots: false,
  includeDates: [], // TODO: move out of zustand
  dates: [], // TODO: move out of zustand
  monthsFetchedMap: new Map(), // TODO: move out of zustand - should be Tanstack Query
  calendarMeta: {}, // TODO: move out of zustand
  selectedMonth: new Date(),
  activeDayAndSelectedMonthDiffer: false,
  handledScroll: false,
}

export const activeStartTimeDefault: ActiveStartTime = {
  startTime: null,
  endTime: null,
  endTimeWithOffset: null,
  remaining: null,
  id: null,
  eventId: null,
}

const localLineItemsDefault: Transformed.LineItem[] = []
const lineItemsToDeleteDefault: Transformed.LineItem[] = []
const additionalInfoResponsesDefault: DataAccessInput.AdditionalInfoResponse[] = []

export const sellerSliceDefault: SellerSlice = {
  _hasHydrated: false,
  doActionContainerScroll: true,
  errorNotification: {
    error: false,
    message: null,
    reload: false,
    promptManualReload: false,
    dismissible: true,
    errorCode: 'Null',
  },
  lastStepCompleted: 0,
  userIsLocal: false,
  activeDate: null,
  selectedTimeslotTime: null,
  selectedTimeslotId: null,
  activeStartTime: activeStartTimeDefault,
  localLineItems: localLineItemsDefault,
  lineItemsToDelete: lineItemsToDeleteDefault,
  additionalInfoResponses: additionalInfoResponsesDefault,
  admissionType: AdmissionTypeDefault,
  paymentMethod: PaymentMethodType.card,
  appliedGiftCards: [],
  complete: false,
  signupForEmail: null,
  giftRecipient: {
    giftRecipientId: '',
    giftMessage: null,
  },
  calendarState: calendarStateDefault,
  cartId: null,
  loadingBlob: {
    visible: false,
    title: TXT.titles.components.loadingBlob.paymentTitle,
  },
  startTimeFromUrlParam: false,
}

/**
 * NOTE: this is too generic and poorly named
 * consider splitting this into multiple slices, one for the exhibition event, one for events seller
 * or figure out how to make it work better for both
 * @param set
 */
export const createSellerSlice: StateCreator<SellerSliceState, [], [], SellerSliceState> = (set) => ({
  ...sellerSliceDefault,
  setHydrated: (value: boolean) => {
    set({ _hasHydrated: value })
  },
  setCartId: (cartId: string, isLoggedIn: boolean) => set((state) => {
    /* istanbul ignore else */
    if (!isLoggedIn) {
      setBrowserCookie(GUEST_CART_IDS_KEY, cartId, { minutesToExpire: 10 })
    }

    return {
      ...state,
      cartId,
    }
  }),
  updateAdditionalInfoResponses: (
    additionalInfoResponses: DataAccessInput.AdditionalInfoResponse[],
  ) => set((state) => {
    const { additionalInfoResponses: existingResponses, ...rest } = state

    const merged = additionalInfoResponses.reduce((acc, response) => {
      const index = acc.findIndex((existingResponse) => (
        existingResponse.id === response.id
      ))

      if (index > -1) {
        // update existing response
        acc[index] = response
      } else {
        // push new response
        acc.push(response)
      }

      return acc
    }, [...existingResponses])

    return {
      ...rest,
      additionalInfoResponses: merged,
    }
  }),
  resetCalendarState: () => set((state) => ({
    ...state,
    calendarState: calendarStateDefault,
  })),
  setCalendarState: (newState: Partial<CalendarState>) => set((state) => {
    const oldState = state.calendarState
    let nextMonthsFetchedMap = oldState.monthsFetchedMap
    let nextCalendarMeta = oldState.calendarMeta

    if (newState.monthsFetchedMap) {
      nextMonthsFetchedMap = new Map([
        ...oldState.monthsFetchedMap,
        ...newState.monthsFetchedMap,
      ])
    }

    if (newState.calendarMeta) {
      nextCalendarMeta = {
        ...oldState.calendarMeta,
        ...newState.calendarMeta,
      }
    }

    return {
      ...state,
      calendarState: {
        ...oldState,
        ...newState,
        monthsFetchedMap: nextMonthsFetchedMap,
        calendarMeta: nextCalendarMeta,
      },
    }
  }),
  /**
   * Resets the admission type and updates the state with relevant changes.
   *
   * @function
   * @param {Object} param0 - An object containing parameters required to reset the admission type.
   * @param {string} param0.admissionType - The type of admission being set.
   * @param {string} [param0.activeStartTime] - Optional new active start time. Defaults to a predefined
   * value if not provided.
   * @returns {Function} A callback function that updates the state with the new admission type and related attributes.
   *
   * This function performs the following operations:
   * - Preserves the current state while modifying specific attributes.
   * - Resets local line items, line items to delete, and additional info responses to their default values.
   * - Optionally updates the active start time, falling back to a default if not provided.
   * - Sets the new admission type. The event being bought is derived from it, see `useSelectedEventId`.
   */
  resetAdmissionType: ({
    admissionType,
    activeStartTime,
  }: ResetAdmissionTypeProps): void => set((state: SellerSliceState): SellerSliceState => ({
    // preserve
    ...state,
    // reset
    localLineItems: localLineItemsDefault,
    lineItemsToDelete: lineItemsToDeleteDefault,
    additionalInfoResponses: additionalInfoResponsesDefault,
    // maybe reset
    activeStartTime: activeStartTime || activeStartTimeDefault,
    // new values
    admissionType,
  })),
  /**
   * Toggles the visibility and updates the title of the loading blob component
   * based on the `visible` and `paymentRequired` parameters.
   *
   * @param {boolean} visible - Determines whether the loading blob is visible.
   * @param {boolean} paymentRequired - Indicates if payment is required to update the loading blob's title accordingly.
   * @returns {void} Updates the state with the modified loading blob visibility and title.
   */
  toggleLoadingBlob: (visible: boolean, paymentRequired: boolean): void => set((state) => {
    const title = paymentRequired
      ? TXT.titles.components.loadingBlob.paymentTitle
      : TXT.titles.components.loadingBlob.noPaymentTitle

    return ({
      ...state,
      loadingBlob: {
        visible,
        title,
      },
    })
  }),
})
